import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Loader2, Plus, Star, Trash2, RefreshCw } from "lucide-react";
import {
    listMailboxes,
    createMailbox,
    updateMailbox,
    deleteMailbox,
    setDefaultMailbox,
    testMailboxSmtp,
    testMailboxImap,
    testRawSmtp,
    testRawImap,
    syncMailbox
} from "../../services/crmServices/mailService";

const PRESETS = {
    custom: { label: "Custom", smtp: { host: "", port: 587, secure: false }, imap: { host: "", port: 993, secure: true } },
    gmail: { label: "Gmail", smtp: { host: "smtp.gmail.com", port: 587, secure: false }, imap: { host: "imap.gmail.com", port: 993, secure: true } },
    outlook: { label: "Outlook / Microsoft 365", smtp: { host: "smtp.office365.com", port: 587, secure: false }, imap: { host: "outlook.office365.com", port: 993, secure: true } },
    yahoo: { label: "Yahoo", smtp: { host: "smtp.mail.yahoo.com", port: 587, secure: false }, imap: { host: "imap.mail.yahoo.com", port: 993, secure: true } }
};

const emptyForm = () => ({
    _id: null,
    label: "",
    fromName: "",
    fromEmail: "",
    signature: "",
    trackingEnabled: true,
    imapSameAsSmtp: true,
    smtp: { host: "", port: 587, secure: false, username: "", password: "" },
    imap: { host: "", port: 993, secure: true, username: "", password: "" }
});

export function MailboxesPanel({ onChanged }) {
    const [boxes, setBoxes] = useState([]);
    const [form, setForm] = useState(emptyForm());
    const [preset, setPreset] = useState("custom");
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState("");

    const load = async () => {
        const res = await listMailboxes();
        setBoxes(res.data || []);
        onChanged?.(res.data || []);
    };

    useEffect(() => {
        load().catch((err) => toast.error(err.response?.data?.message || "Failed to load mailboxes"));
    }, []);

    const applyPreset = (key) => {
        setPreset(key);
        const p = PRESETS[key];
        if (!p) return;
        setForm((f) => ({
            ...f,
            smtp: { ...f.smtp, ...p.smtp },
            imap: { ...f.imap, ...p.imap }
        }));
    };

    const edit = (box) => {
        setForm({
            _id: box._id,
            label: box.label || "",
            fromName: box.fromName || "",
            fromEmail: box.fromEmail || "",
            signature: box.signature || "",
            trackingEnabled: box.trackingEnabled !== false,
            imapSameAsSmtp: box.imapSameAsSmtp !== false,
            smtp: { host: box.smtp?.host || "", port: box.smtp?.port || 587, secure: !!box.smtp?.secure, username: box.smtp?.username || "", password: "" },
            imap: { host: box.imap?.host || "", port: box.imap?.port || 993, secure: box.imap?.secure !== false, username: box.imap?.username || "", password: "" }
        });
    };

    const save = async () => {
        if (!form.label || !form.smtp.host || !form.smtp.username) {
            return toast.error("Label, SMTP host and username are required");
        }
        if (!form._id && !form.smtp.password) return toast.error("SMTP password is required");
        setSaving(true);
        try {
            const payload = { ...form };
            if (!payload.smtp.password) delete payload.smtp.password;
            if (!payload.imap.password) delete payload.imap.password;
            const res = form._id ? await updateMailbox(form._id, payload) : await createMailbox(payload);
            if (res.status !== 200) throw new Error(res.message);
            toast.success("Mailbox saved");
            setForm(emptyForm());
            setPreset("custom");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const runTest = async (kind) => {
        setTesting(kind);
        try {
            let res;
            if (form._id && (kind === "smtp" || kind === "imap")) {
                res = kind === "smtp" ? await testMailboxSmtp(form._id) : await testMailboxImap(form._id);
            } else if (kind === "smtp") {
                res = await testRawSmtp({
                    ...form.smtp,
                    fromEmail: form.fromEmail,
                    fromName: form.fromName
                });
            } else {
                const creds = form.imapSameAsSmtp
                    ? { host: form.imap.host || form.smtp.host.replace(/^smtp\./i, "imap."), port: form.imap.port, secure: form.imap.secure, username: form.smtp.username, password: form.smtp.password }
                    : form.imap;
                res = await testRawImap(creds);
            }
            if (res.status !== 200) throw new Error(res.message);
            toast.success(kind === "smtp" ? "Test email sent to your address" : "IMAP connected");
            if (form._id) await load();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Test failed");
        } finally {
            setTesting("");
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 h-full min-h-0">
            <div className="lg:col-span-2 space-y-2 overflow-y-auto">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Your mailboxes</h3>
                    <Button size="sm" variant="outline" onClick={() => setForm(emptyForm())}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> New
                    </Button>
                </div>
                {boxes.length === 0 && (
                    <p className="text-sm text-gray-500">Add the email you send from. Company SMTP is not used — each agent connects their own account.</p>
                )}
                {boxes.map((b) => (
                    <button
                        key={b._id}
                        type="button"
                        onClick={() => edit(b)}
                        className={`w-full text-left rounded-lg border px-3 py-2 ${form._id === b._id ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{b.label}</span>
                            {b.isDefault && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                        </div>
                        <p className="text-xs text-gray-500">{b.fromEmail || b.smtp?.username}</p>
                        <p className="text-[11px] text-gray-400 mt-1">
                            SMTP {b.smtpStatus === "ok" ? "ok" : b.smtpStatus} · IMAP {b.imapStatus === "ok" ? "ok" : b.imapStatus}
                            {b.lastError ? ` · ${b.lastError}` : ""}
                        </p>
                    </button>
                ))}
            </div>

            <div className="lg:col-span-3 overflow-y-auto bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <Label className="text-xs">Provider preset</Label>
                        <select className="mt-1 w-full h-9 rounded-md border px-2 text-sm" value={preset} onChange={(e) => applyPreset(e.target.value)}>
                            {Object.entries(PRESETS).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-gray-400 mt-1">Gmail/Yahoo need an app password, not your normal login password.</p>
                    </div>
                    <div>
                        <Label className="text-xs">Label</Label>
                        <Input className="h-9 mt-1" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Work Gmail" />
                    </div>
                    <div>
                        <Label className="text-xs">From name</Label>
                        <Input className="h-9 mt-1" value={form.fromName} onChange={(e) => setForm({ ...form, fromName: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-xs">From email</Label>
                        <Input className="h-9 mt-1" value={form.fromEmail} onChange={(e) => setForm({ ...form, fromEmail: e.target.value })} />
                    </div>
                    <div className="col-span-2 text-xs font-semibold text-gray-600 pt-1">SMTP (send)</div>
                    <div>
                        <Label className="text-xs">Host</Label>
                        <Input className="h-9 mt-1" value={form.smtp.host} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, host: e.target.value } })} />
                    </div>
                    <div>
                        <Label className="text-xs">Port</Label>
                        <Input className="h-9 mt-1" type="number" value={form.smtp.port} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, port: e.target.value } })} />
                    </div>
                    <div>
                        <Label className="text-xs">Username</Label>
                        <Input className="h-9 mt-1" value={form.smtp.username} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, username: e.target.value } })} />
                    </div>
                    <div>
                        <Label className="text-xs">Password</Label>
                        <Input className="h-9 mt-1" type="password" value={form.smtp.password} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, password: e.target.value } })} placeholder={form._id ? "Leave blank to keep" : ""} />
                    </div>
                    <label className="col-span-2 flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.smtp.secure} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, secure: e.target.checked } })} />
                        SSL on connect (usually port 465 only)
                    </label>
                    <label className="col-span-2 flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.imapSameAsSmtp} onChange={(e) => setForm({ ...form, imapSameAsSmtp: e.target.checked })} />
                        IMAP uses the same username and password
                    </label>
                    <div className="col-span-2 text-xs font-semibold text-gray-600 pt-1">IMAP (inbox)</div>
                    <div>
                        <Label className="text-xs">Host</Label>
                        <Input className="h-9 mt-1" value={form.imap.host} onChange={(e) => setForm({ ...form, imap: { ...form.imap, host: e.target.value } })} />
                    </div>
                    <div>
                        <Label className="text-xs">Port</Label>
                        <Input className="h-9 mt-1" type="number" value={form.imap.port} onChange={(e) => setForm({ ...form, imap: { ...form.imap, port: e.target.value } })} />
                    </div>
                    {!form.imapSameAsSmtp && (
                        <>
                            <div>
                                <Label className="text-xs">Username</Label>
                                <Input className="h-9 mt-1" value={form.imap.username} onChange={(e) => setForm({ ...form, imap: { ...form.imap, username: e.target.value } })} />
                            </div>
                            <div>
                                <Label className="text-xs">Password</Label>
                                <Input className="h-9 mt-1" type="password" value={form.imap.password} onChange={(e) => setForm({ ...form, imap: { ...form.imap, password: e.target.value } })} />
                            </div>
                        </>
                    )}
                    <div className="col-span-2">
                        <Label className="text-xs">Signature (HTML ok)</Label>
                        <Textarea className="mt-1 text-sm" rows={3} value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} />
                    </div>
                    <label className="col-span-2 flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.trackingEnabled} onChange={(e) => setForm({ ...form, trackingEnabled: e.target.checked })} />
                        Track opens and link clicks
                    </label>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                    <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700">
                        {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save mailbox
                    </Button>
                    <Button variant="outline" onClick={() => runTest("smtp")} disabled={!!testing}>
                        {testing === "smtp" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Test SMTP
                    </Button>
                    <Button variant="outline" onClick={() => runTest("imap")} disabled={!!testing}>
                        {testing === "imap" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Test IMAP
                    </Button>
                    {form._id && (
                        <>
                            <Button variant="outline" onClick={() => setDefaultMailbox(form._id).then(load)}>
                                <Star className="h-4 w-4 mr-1" /> Default
                            </Button>
                            <Button variant="outline" onClick={() => syncMailbox(form._id).then((r) => { toast.success(r.message || "Synced"); load(); })}>
                                <RefreshCw className="h-4 w-4 mr-1" /> Sync now
                            </Button>
                            <Button
                                variant="outline"
                                className="text-red-600"
                                onClick={async () => {
                                    await deleteMailbox(form._id);
                                    setForm(emptyForm());
                                    toast.success("Deleted");
                                    load();
                                }}
                            >
                                <Trash2 className="h-4 w-4 mr-1" /> Delete
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
